// Create / edit a project (building). One dialog serves both the grouped
// "Dự án" view (add / edit a project) and the property wizard's inline
// "＋ Tạo dự án" (create then auto-select).
//
// The shared fields entered here (name, ward, address, total floors) are
// live-linked: editing them propagates to every unit in the project on save
// (backend `syncFromProject`). The rest (handover year, basements, service /
// parking fees, code prefix) live on the project only.
//
// Clearing semantics mirror the backend PATCH: text fields clear when emptied;
// numeric fields keep their previous value when the box is emptied (there's no
// "unset a number" path yet — a deliberate v1 simplification).
//
// The form lives in an inner <ProjectFormBody> so it re-initialises from props
// on each open (Radix unmounts dialog content on close) — no reset effect.

import { useState } from "react";
import { Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { WardCombobox } from "@/components/ward-combobox";
import {
  useCreateProject,
  useDeleteProject,
  useUpdateProject,
  type Project,
  type ProjectInput,
} from "@/features/project/api";

interface FormState {
  name: string;
  codePrefix: string;
  completionYear: string;
  wardCode: string;
  streetAddress: string;
  totalFloors: string;
  basements: string;
  serviceFee: string;
  parkingFeeCar: string;
  parkingFeeMotorbike: string;
}

function emptyForm(): FormState {
  return {
    name: "",
    codePrefix: "",
    completionYear: "",
    wardCode: "",
    streetAddress: "",
    totalFloors: "",
    basements: "",
    serviceFee: "",
    parkingFeeCar: "",
    parkingFeeMotorbike: "",
  };
}

function formFromProject(p: Project): FormState {
  return {
    name: p.name,
    codePrefix: p.codePrefix ?? "",
    completionYear: p.completionYear != null ? String(p.completionYear) : "",
    wardCode: p.wardCode ?? "",
    streetAddress: p.streetAddress ?? "",
    totalFloors: p.totalFloors != null ? String(p.totalFloors) : "",
    basements: p.basements != null ? String(p.basements) : "",
    serviceFee: p.serviceFee != null ? String(p.serviceFee) : "",
    parkingFeeCar: p.parkingFeeCar != null ? String(p.parkingFeeCar) : "",
    parkingFeeMotorbike:
      p.parkingFeeMotorbike != null ? String(p.parkingFeeMotorbike) : "",
  };
}

/** Empty string → null; otherwise a finite number, or null when unparseable. */
function toNum(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function ProjectFormDialog({
  open,
  onOpenChange,
  project,
  initialName,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode; absent → create mode. */
  project?: Project | undefined;
  /** Seed the name in create mode (the wizard passes the typed-in text). */
  initialName?: string | undefined;
  /** Called with the saved project after a successful create / update. */
  onSaved?: ((project: Project) => void) | undefined;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <ProjectFormBody
          project={project}
          initialName={initialName}
          onDone={() => onOpenChange(false)}
          onSaved={onSaved}
        />
      </DialogContent>
    </Dialog>
  );
}

function ProjectFormBody({
  project,
  initialName,
  onDone,
  onSaved,
}: {
  project?: Project | undefined;
  initialName?: string | undefined;
  onDone: () => void;
  onSaved?: ((project: Project) => void) | undefined;
}) {
  const isEdit = project !== undefined;
  const [form, setForm] = useState<FormState>(() =>
    project ? formFromProject(project) : { ...emptyForm(), name: initialName ?? "" },
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject(project?.id ?? 0);
  const deleteMutation = useDeleteProject();
  const saving = createMutation.isPending || updateMutation.isPending;

  // Deleting is owner/manager-only (the backend enforces it); a Source can edit
  // but not delete, so hide the button rather than let it 403.
  const role = useAuthStore((s) => s.user?.role);
  const canDelete = role === "OWNER" || role === "MANAGER";

  const set = (patch: Partial<FormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = () => {
    setNameError(null);
    setFormError(null);
    if (form.name.trim() === "") {
      setNameError("Vui lòng nhập tên dự án");
      return;
    }
    const body: ProjectInput = {
      name: form.name.trim(),
      wardCode: form.wardCode,
      streetAddress: form.streetAddress.trim(),
      totalFloors: toNum(form.totalFloors),
      basements: toNum(form.basements),
      completionYear: toNum(form.completionYear),
      serviceFee: toNum(form.serviceFee),
      parkingFeeCar: toNum(form.parkingFeeCar),
      parkingFeeMotorbike: toNum(form.parkingFeeMotorbike),
      codePrefix: form.codePrefix.trim(),
    };

    const onError = (err: unknown) => {
      if (
        err instanceof ApiError &&
        (err.body?.code === "PROJECT_NAME_DUPLICATE" || err.status === 409)
      ) {
        setNameError("Tên dự án này đã tồn tại");
      } else {
        setFormError("Không thể lưu dự án. Vui lòng thử lại.");
      }
    };
    const onSuccess = (saved: Project) => {
      onSaved?.(saved);
      onDone();
    };

    if (isEdit) {
      updateMutation.mutate(body, { onSuccess, onError });
    } else {
      createMutation.mutate(body, { onSuccess, onError });
    }
  };

  const handleDelete = () => {
    if (!project) return;
    setDeleteError(null);
    deleteMutation.mutate(project.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        onDone();
      },
      onError: () => {
        setDeleteError("Không thể xóa dự án. Vui lòng thử lại.");
      },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Sửa dự án" : "Tạo dự án mới"}</DialogTitle>
        <DialogDescription>
          Thông tin chung của dự án. Các trường dùng chung (tên, khu vực, địa
          chỉ, tổng số tầng) sẽ tự cập nhật cho mọi căn thuộc dự án khi lưu.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <Field label="Tên dự án" htmlFor="pf-name" required error={nameError}>
          <Input
            id="pf-name"
            placeholder="VD: Tây Hồ Residence"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            aria-invalid={nameError !== null}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Tiền tố mã căn"
            htmlFor="pf-prefix"
            hint="Tự đánh số mã căn, VD: FLC → FLC-001"
          >
            <Input
              id="pf-prefix"
              placeholder="FLC"
              value={form.codePrefix}
              onChange={(e) => set({ codePrefix: e.target.value.toUpperCase() })}
            />
          </Field>
          <Field label="Năm bàn giao" htmlFor="pf-year">
            <Input
              id="pf-year"
              type="number"
              inputMode="numeric"
              placeholder="2025"
              value={form.completionYear}
              onChange={(e) => set({ completionYear: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Khu vực" htmlFor="pf-ward">
            <WardCombobox
              id="pf-ward"
              value={form.wardCode === "" ? null : form.wardCode}
              onChange={(v) => set({ wardCode: v ?? "" })}
              clearable
              clearLabel="Không chọn"
            />
          </Field>
          <Field label="Địa chỉ" htmlFor="pf-address">
            <Input
              id="pf-address"
              placeholder="Số nhà, tên đường"
              value={form.streetAddress}
              onChange={(e) => set({ streetAddress: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Tổng số tầng" htmlFor="pf-floors">
            <Input
              id="pf-floors"
              type="number"
              inputMode="numeric"
              placeholder="30"
              value={form.totalFloors}
              onChange={(e) => set({ totalFloors: e.target.value })}
            />
          </Field>
          <Field label="Số tầng hầm" htmlFor="pf-basements">
            <Input
              id="pf-basements"
              type="number"
              inputMode="numeric"
              placeholder="2"
              value={form.basements}
              onChange={(e) => set({ basements: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Phí dịch vụ" htmlFor="pf-servicefee" hint="đồng">
          <Input
            id="pf-servicefee"
            type="number"
            inputMode="numeric"
            placeholder="15000"
            value={form.serviceFee}
            onChange={(e) => set({ serviceFee: e.target.value })}
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Phí gửi ô tô" htmlFor="pf-car" hint="đồng">
            <Input
              id="pf-car"
              type="number"
              inputMode="numeric"
              placeholder="1200000"
              value={form.parkingFeeCar}
              onChange={(e) => set({ parkingFeeCar: e.target.value })}
            />
          </Field>
          <Field label="Phí gửi xe máy" htmlFor="pf-moto" hint="đồng">
            <Input
              id="pf-moto"
              type="number"
              inputMode="numeric"
              placeholder="120000"
              value={form.parkingFeeMotorbike}
              onChange={(e) => set({ parkingFeeMotorbike: e.target.value })}
            />
          </Field>
        </div>

        {formError !== null ? (
          <p className="text-[14px] text-destructive" role="alert">
            {formError}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        {project && canDelete ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setDeleteError(null);
              setConfirmDelete(true);
            }}
            disabled={saving || deleteMutation.isPending}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive sm:mr-auto"
          >
            <Trash2 aria-hidden="true" />
            Xóa dự án
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onDone} disabled={saving}>
          Huỷ
        </Button>
        <Button type="button" variant="primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Đang lưu…" : isEdit ? "Lưu thay đổi" : "Tạo dự án"}
        </Button>
      </DialogFooter>

      {project ? (
        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Xóa dự án?</AlertDialogTitle>
              <AlertDialogDescription>
                Dự án &quot;{project.name}&quot; sẽ bị xóa.{" "}
                {project.unitCount > 0
                  ? `${project.unitCount} căn thuộc dự án sẽ được giữ lại nhưng không còn thuộc dự án nào.`
                  : "Dự án này chưa có căn nào."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {deleteError !== null ? (
              <p className="text-[14px] text-destructive" role="alert">
                {deleteError}
              </p>
            ) : null}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>
                Huỷ
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete();
                }}
                disabled={deleteMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? "Đang xóa…" : "Xóa dự án"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------

function Field({
  label,
  htmlFor,
  hint,
  required,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-[14px] font-medium">
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
      {error != null ? (
        <p className="text-[12px] text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
